import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Cpu,
  Gauge,
  Layers,
  Loader2,
  Play,
  SkipForward,
} from "lucide-react";

import type { GameScenario } from "../../data/scenarios/types";
import {
  GAME_LIBRARY,
  type GameConfig,
  prepareScenarios,
  resolveGameOutcome,
  stripLineBreakTags,
} from "../../data/scenarios/config";
import { LineBreakText } from "./LineBreakText";
import { InactivityModal } from "./InactivityModal";
import { SelectionVideoModal } from "./SelectionVideoModal";

interface MissionExperienceProps {
  gameType: string | null;
  participantLabel: string;
  onExit: () => void;
}

type GamePhase = "selection" | "result" | "complete";

type MissionView = "intro" | "game" | "complete";

interface OutcomeStats {
  total: number;
  success: number;
  fail: number;
}

interface CaptionCardProps {
  content?: string;
  label: string;
}

// 캡션 카드 컴포넌트
const CaptionCard = ({ content, label }: CaptionCardProps) => {
  if (!content) {
    return null;
  }
  return (
    <div className="mt-[60px] w-full">
      <div className="relative mx-auto max-w-7xl rounded-[1.25rem] border-4 border-cyan-400/70 bg-gradient-to-br from-slate-800/90 via-slate-800/80 to-slate-900/90 pt-[40px] pb-[30px] px-[50px] shadow-[0_35px_120px_rgba(6,182,212,0.3)] ring-2 ring-cyan-500/20 backdrop-blur-sm">
        <div className="absolute -top-[2rem] left-10">
          <span className="inline-flex items-center rounded-[1.25rem] bg-cyan-500 px-[25px] py-[15px] text-xl font-semibold text-white shadow-lg shadow-cyan-500/30">
            {label}
          </span>
        </div>
        <div className="space-y-2 text-white">
          <p className="text-[1.75rem] font-semibold leading-relaxed">{content}</p>
        </div>
      </div>
    </div>
  );
};

const computeOutcomeTheme = (stats: OutcomeStats) => {
  const fail = stats.fail;
  if (fail <= 1) {
    return {
      badge: "bg-yellow-500/20 border-cyan-400/50 text-yellow-300",
      border: "border-4 border-yellow-500/50",
      headingColor: "text-white",
      headingShadow: "drop-shadow-[0_8px_22px_rgba(234,179,8,0.4)]",
    };
  }
  if (fail <= 3) {
    return {
      badge: "bg-cyan-500/20 border-cyan-400/50 text-cyan-300",
      border: "border-4 border-cyan-500/50",
      headingColor: "text-white",
      headingShadow: "drop-shadow-[0_8px_22px_rgba(6,182,212,0.35)]",
    };
  }
  return {
    badge: "bg-orange-500/20 border-orange-400/50 text-orange-300",
    border: "border-4 border-orange-500/50",
    headingColor: "text-white",
    headingShadow: "drop-shadow-[0_8px_22px_rgba(249,115,22,0.35)]",
  };
};

export const MissionExperience = ({
  gameType,
  participantLabel,
  onExit,
}: MissionExperienceProps) => {
  const gameConfig: GameConfig | null = useMemo(
    () => (gameType ? GAME_LIBRARY[gameType] ?? null : null),
    [gameType]
  );

  const [view, setView] = useState<MissionView>("intro");
  const [scenarios, setScenarios] = useState<GameScenario[]>([]);
  const [currentScene, setCurrentScene] = useState(0);
  const [selectedChoiceIndex, setSelectedChoiceIndex] = useState<number | null>(
    null
  );
  const [gamePhase, setGamePhase] = useState<GamePhase>("selection");
  const [isResultVideoComplete, setIsResultVideoComplete] = useState(false);
  const [isResultVideoPlaying, setIsResultVideoPlaying] = useState(false);
  const [selectionVideoReady, setSelectionVideoReady] = useState<
    Record<string, boolean>
  >({});
  const [playingSelectionVideos, setPlayingSelectionVideos] = useState<
    Record<string, boolean>
  >({});
  const [watchedSelectionVideos, setWatchedSelectionVideos] = useState<
    Record<string, boolean>
  >({});
  const [pendingChoiceIndex, setPendingChoiceIndex] = useState<number | null>(
    null
  );
  const [optimalChoiceCount, setOptimalChoiceCount] = useState(0);
  const [completedChoiceCount, setCompletedChoiceCount] = useState(0);
  const [finalOutcome, setFinalOutcome] = useState<
    (ReturnType<typeof resolveGameOutcome> & { stats: OutcomeStats }) | null
  >(null);
  const [isFinalVideoComplete, setIsFinalVideoComplete] = useState(false);
  const [isFinalVideoPlaying, setIsFinalVideoPlaying] = useState(false);
  const [isIntroVideoPlaying, setIsIntroVideoPlaying] = useState(false);
  const [introPlaybackRate, setIntroPlaybackRate] = useState(1);
  const [isIntroVideoReady, setIsIntroVideoReady] = useState(false);
  const [showInactivityModal, setShowInactivityModal] = useState(false);
  const [inactivityModalCountdown, setInactivityModalCountdown] = useState(5);
  const [modalSelectionIndex, setModalSelectionIndex] = useState<number | null>(
    null
  );

  const introVideoRef = useRef<HTMLVideoElement | null>(null);
  const selectionVideoRefs = useRef<Record<string, HTMLVideoElement | null>>(
    {}
  );
  const resultVideoRef = useRef<HTMLVideoElement | null>(null);
  const finalVideoRef = useRef<HTMLVideoElement | null>(null);
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const inactivityModalTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityTimeRef = useRef<number>(Date.now());

  const resetState = () => {
    setCurrentScene(0);
    setSelectedChoiceIndex(null);
    setGamePhase("selection");
    setIsResultVideoComplete(false);
    setIsResultVideoPlaying(false);
    setSelectionVideoReady({});
    setPlayingSelectionVideos({});
    setWatchedSelectionVideos({});
    setPendingChoiceIndex(null);
    setOptimalChoiceCount(0);
    setCompletedChoiceCount(0);
    setFinalOutcome(null);
    setIsFinalVideoComplete(false);
    setIsFinalVideoPlaying(false);
    setIsIntroVideoPlaying(false);
    setIntroPlaybackRate(1);
    setIsIntroVideoReady(false);
    setView("intro");
    setShowInactivityModal(false);
    setInactivityModalCountdown(5);
    setModalSelectionIndex(null);
    lastActivityTimeRef.current = Date.now();
  };

  // 모든 선택지 비디오 일시정지
  const pauseAllSelectionVideos = () => {
    Object.values(selectionVideoRefs.current).forEach((video) => {
      if (!video) {
        return;
      }
      try {
        video.pause();
        video.currentTime = 0;
      } catch (error) {
        console.error("비디오 일시정지 실패:", error);
      }
    });
    setPlayingSelectionVideos({});
  };

  // 안전한 비디오 재생 함수
  const safePlayVideo = async (video: HTMLVideoElement): Promise<boolean> => {
    if (!video) {
      return false;
    }

    try {
      video.pause();
      await new Promise((resolve) => setTimeout(resolve, 50));

      await video.play();
      return true;
    } catch (error) {
      // AbortError는 의도적인 중단이므로 정상 케이스로 처리
      if (error instanceof Error && error.name === "AbortError") {
        return false;
      }
      // 다른 에러는 로그만 남기고 실패 처리
      console.error("비디오 재생 실패:", error);
      return false;
    }
  };

  // 게임 설정 변경 시 상태 초기화
  useEffect(() => {
    if (!gameConfig) {
      return;
    }
    setScenarios(prepareScenarios(gameConfig.scenarios));
    resetState();
    pauseAllSelectionVideos();
  }, [gameConfig]);

  // 인트로 비디오 재생
  useEffect(() => {
    if (view !== "intro" || !introVideoRef.current) {
      return;
    }
    const video = introVideoRef.current;

    const playVideo = async () => {
      try {
        if (video.readyState < 2) {
          await new Promise((resolve) => {
            const handleLoadedData = () => {
              video.removeEventListener("loadeddata", handleLoadedData);
              resolve(undefined);
            };
            video.addEventListener("loadeddata", handleLoadedData);
            if (video.readyState >= 2) {
              video.removeEventListener("loadeddata", handleLoadedData);
              resolve(undefined);
            }
          });
        }

        video.playbackRate = introPlaybackRate;
        await safePlayVideo(video);
      } catch (error) {
        if (error instanceof Error && error.name !== "AbortError") {
          console.error("인트로 영상 재생 실패:", error);
        }
      }
    };

    playVideo();
  }, [view, gameConfig?.introVideo, introPlaybackRate]);

  const totalScenes = scenarios.length;
  const currentScenario =
    totalScenes > 0 && currentScene >= 0 && currentScene < totalScenes
      ? scenarios[currentScene]
      : undefined;
  const choices: GameScenario["choices"] = Array.isArray(
    currentScenario?.choices
  )
    ? (currentScenario?.choices as GameScenario["choices"])
    : [];
  const selectedChoice =
    selectedChoiceIndex !== null ? choices[selectedChoiceIndex] : undefined;

  // 결과 비디오 자동 재생
  useEffect(() => {
    if (
      gamePhase !== "result" ||
      !resultVideoRef.current ||
      !selectedChoice?.video?.result
    ) {
      return;
    }
    const video = resultVideoRef.current;
    video.muted = false;
    const playVideo = async () => {
      const success = await safePlayVideo(video);
      if (success) {
        setIsResultVideoComplete(false);
      }
    };
    playVideo();
  }, [gamePhase, selectedChoice?.video?.result]);

  const stats: OutcomeStats = finalOutcome?.stats ?? {
    total: completedChoiceCount,
    success: optimalChoiceCount,
    fail: completedChoiceCount - optimalChoiceCount,
  };

  const outcomeTheme = computeOutcomeTheme(stats);
  const failIsZero = stats.fail === 0;
  const failStatTextClass = failIsZero ? "text-emerald-300" : "text-orange-300";
  const failStatBorderClass = failIsZero
    ? "border-emerald-500/50"
    : "border-orange-500/50";
  const failStatIconClass = failIsZero ? "text-emerald-400" : "text-orange-400";
  const progressPercent =
    totalScenes > 0
      ? Math.min(100, ((currentScene + 1) / totalScenes) * 100)
      : 0;

  const successLabel = gameConfig?.successLabel ?? "AI 전략";
  const failLabel = gameConfig?.failLabel ?? "기타 선택";
  const missionLabel = stripLineBreakTags(gameConfig?.name) || participantLabel;

  const finalStatItems = [
    {
      id: "total",
      text: `총 ${stats.total} 스테이지`,
      icon: Layers,
      chipClass:
        "border border-cyan-500/50 bg-slate-700/60 hover:bg-slate-700/80",
      textClass: "text-cyan-300",
      iconClass: "text-cyan-400",
      iconWrapClass: "bg-cyan-500/20",
    },
    {
      id: "success",
      text: `${successLabel} ${stats.success}`,
      icon: Cpu,
      chipClass:
        "border border-cyan-500/50 bg-slate-700/60 hover:bg-slate-700/80",
      textClass: "text-cyan-300",
      iconClass: "text-cyan-400",
      iconWrapClass: "bg-cyan-500/20",
    },
    {
      id: "fail",
      text: `${failLabel} ${stats.fail}`,
      icon: AlertTriangle,
      chipClass: `border ${failStatBorderClass} bg-slate-700/60 hover:bg-slate-700/80`,
      textClass: failStatTextClass,
      iconClass: failStatIconClass,
      iconWrapClass: failIsZero ? "bg-emerald-500/20" : "bg-orange-500/20",
    },
  ];

  // 선택지 비디오 재생
  const handleSelectionVideoOverlayClick = async (
    event: React.MouseEvent<HTMLButtonElement>,
    key: string
  ) => {
    event.preventDefault();
    event.stopPropagation();
    
    const parts = key.split("-");
    const choiceIndex = parts.length > 1 ? parseInt(parts[1], 10) : null;
    
    if (choiceIndex === null || isNaN(choiceIndex)) {
      return;
    }

    // 모든 선택지 비디오 일시정지
    pauseAllSelectionVideos();

    // 모달 열기
    setModalSelectionIndex(choiceIndex);
  };

  // 선택지 비디오 일시정지/재생
  const handleSelectionVideoAreaClick = (
    event: React.MouseEvent<HTMLDivElement>,
    refKey: string,
    choiceIndex: number
  ) => {
    if (
      event.target instanceof HTMLElement &&
      (event.target.tagName === "BUTTON" || event.target.closest("button"))
    ) {
      return;
    }

    // 모든 선택지 비디오 일시정지
    pauseAllSelectionVideos();

    // 모달 열기
    setModalSelectionIndex(choiceIndex);
  };

  // 모달 닫기
  const handleCloseModal = () => {
    setModalSelectionIndex(null);
  };

  // 선택지 비디오 스킵
  const handleSelectionVideoSkip = (choiceIndex: number) => {
    const refKey = `${currentScene}-${choiceIndex}`;
    const selectionVideo = selectionVideoRefs.current[refKey];

    if (selectionVideo) {
      if (selectionVideo.duration && !isNaN(selectionVideo.duration)) {
        selectionVideo.currentTime = selectionVideo.duration;
      }
      selectionVideo.pause();

      setPlayingSelectionVideos((prev) => ({
        ...prev,
        [refKey]: false,
      }));
      // 스킵해도 영상을 시청한 것으로 간주
      setWatchedSelectionVideos((prev) => ({
        ...prev,
        [refKey]: true,
      }));

      if (pendingChoiceIndex === choiceIndex) {
        const choice = currentScenario?.choices?.[choiceIndex];
        if (choice) {
          setPendingChoiceIndex(null);
          setSelectedChoiceIndex(choiceIndex);
          setGamePhase("result");
          setIsResultVideoComplete(!choice?.video?.result);
          setIsResultVideoPlaying(false);
        }
      }
    }
  };

  // 결과 비디오 스킵
  const handleResultVideoSkip = () => {
    if (resultVideoRef.current) {
      const video = resultVideoRef.current;
      if (video.duration && !isNaN(video.duration)) {
        video.currentTime = video.duration;
      }
      video.pause();
      setIsResultVideoComplete(true);
      setIsResultVideoPlaying(false);
    }
  };

  // 결과 비디오 일시정지/재생
  const handleResultVideoAreaClick = (
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    if (
      event.target instanceof HTMLElement &&
      (event.target.tagName === "BUTTON" || event.target.closest("button"))
    ) {
      return;
    }

    const video = resultVideoRef.current;
    if (!video) {
      return;
    }

    const isAtEnd =
      video.duration &&
      !isNaN(video.duration) &&
      video.currentTime >= video.duration - 0.1;

    if (video.paused) {
      if (isAtEnd) {
        return;
      }
      safePlayVideo(video).then((success) => {
        if (success) {
          setIsResultVideoPlaying(true);
        }
      });
    } else {
      video.pause();
      setIsResultVideoPlaying(false);
    }
  };

  // 최종 결과 비디오 스킵
  const handleFinalVideoSkip = () => {
    if (finalVideoRef.current) {
      const video = finalVideoRef.current;
      if (video.duration && !isNaN(video.duration)) {
        video.currentTime = video.duration;
      }
      video.pause();
      setIsFinalVideoComplete(true);
      setIsFinalVideoPlaying(false);
    }
  };

  // 완료 영상 일시정지/재생
  const handleFinalVideoAreaClick = (
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    if (
      event.target instanceof HTMLElement &&
      (event.target.tagName === "BUTTON" || event.target.closest("button"))
    ) {
      return;
    }

    const video = finalVideoRef.current;
    if (!video) {
      return;
    }

    const isAtEnd =
      video.duration &&
      !isNaN(video.duration) &&
      video.currentTime >= video.duration - 0.1;

    if (video.paused) {
      if (isAtEnd) {
        return;
      }
      safePlayVideo(video).then((success) => {
        if (success) {
          setIsFinalVideoPlaying(true);
        }
      });
    } else {
      video.pause();
      setIsFinalVideoPlaying(false);
    }
  };

  // 선택지 비디오 재생 확인
  const handleChoiceConfirm = async (choiceIndex: number) => {
    const choice = currentScenario?.choices?.[choiceIndex];
    if (!choice) {
      return;
    }

    pauseAllSelectionVideos();

    // 선택된 선택지의 비디오 재생
    const refKey = `${currentScene}-${choiceIndex}`;
    const hasWatched = watchedSelectionVideos[refKey];

    // 재생 경험이 있는 경우 바로 결과 단계로 이동
    if (hasWatched) {
      setPendingChoiceIndex(null);
      setSelectedChoiceIndex(choiceIndex);
      setGamePhase("result");
      setIsResultVideoComplete(!choice?.video?.result);
      setIsResultVideoPlaying(false);
      return;
    }

    // 재생 경험이 없는 경우 모달을 열어서 크게 재생
    if (choice.video?.selection) {
      setPendingChoiceIndex(choiceIndex);
      // 모달 열기
      setModalSelectionIndex(choiceIndex);
    } else {
      // 비디오가 없는 경우 바로 result 단계로 전환
      setPendingChoiceIndex(null);
      setSelectedChoiceIndex(choiceIndex);
      setGamePhase("result");
      setIsResultVideoComplete(!choice?.video?.result);
      setIsResultVideoPlaying(false);
    }
  };

  // 결과 단계에서 선택지 단계로 돌아가기
  const handleResultGoBack = () => {
    if (resultVideoRef.current) {
      resultVideoRef.current.pause();
      resultVideoRef.current.currentTime = 0;
    }
    setPendingChoiceIndex(null);
    setSelectedChoiceIndex(null);
    setGamePhase("selection");
    setIsResultVideoComplete(false);
    setIsResultVideoPlaying(false);
  };


  // 결과 비디오 재생 확인
  const handleResultContinue = () => {
    if (selectedChoiceIndex === null) {
      setGamePhase("selection");
      return;
    }
    const choice = currentScenario?.choices?.[selectedChoiceIndex];
    if (!choice || typeof choice.next !== "number") {
      setGamePhase("selection");
      setSelectedChoiceIndex(null);
      pauseAllSelectionVideos();
      return;
    }

    const updatedCompletedCount = completedChoiceCount + 1;
    const updatedOptimalCount = optimalChoiceCount + (choice.isOptimal ? 1 : 0);
    const failCount = updatedCompletedCount - updatedOptimalCount;

    setCompletedChoiceCount(updatedCompletedCount);
    setOptimalChoiceCount(updatedOptimalCount);

    if (choice.next === -1) {
      const outcome = resolveGameOutcome(gameType ?? "", { fail: failCount });
      const finalStats: OutcomeStats = {
        total: updatedCompletedCount,
        success: updatedOptimalCount,
        fail: failCount,
      };
      setFinalOutcome(
        outcome
          ? { ...outcome, stats: finalStats }
          : {
              minFail: 0,
              maxFail: Number.POSITIVE_INFINITY,
              title: "작전 결과 요약",
              descriptionTitle: "결과 구성이 준비되지 않았습니다.",
              description:
                "게임 결과 구성이 아직 추가되지 않았습니다. 관리자에게 문의해주세요.",
              video: undefined,
              stats: finalStats,
            }
      );
      setIsFinalVideoComplete(!outcome?.video);
      setSelectedChoiceIndex(null);
      setGamePhase("selection");
      setIsResultVideoComplete(false);
      setIsResultVideoPlaying(false);
      pauseAllSelectionVideos();
      setView("complete");
      return;
    }

    setCurrentScene(choice.next);
    setSelectedChoiceIndex(null);
    setGamePhase("selection");
    setIsResultVideoComplete(false);
    setIsResultVideoPlaying(false);
    pauseAllSelectionVideos();
    selectionVideoRefs.current = {};
  };

  // 인트로 완료 시 비디오 재생
  const handleIntroComplete = () => {
    pauseAllSelectionVideos();
    setView("game");
  };

  // 인트로 비디오 재생 버튼 클릭
  const handleIntroVideoPlay = async () => {
    if (introVideoRef.current) {
      const video = introVideoRef.current;

      const isAtEnd =
        video.duration &&
        !isNaN(video.duration) &&
        video.currentTime >= video.duration - 0.1;

      if (isAtEnd) {
        video.currentTime = 0;
      }

      video.playbackRate = introPlaybackRate;
      await safePlayVideo(video);
    }
  };

  // 인트로 비디오 스킵
  const handleIntroSkip = () => {
    if (introVideoRef.current) {
      const video = introVideoRef.current;
      if (video.duration) {
        if (video.duration && !isNaN(video.duration)) {
          video.currentTime = video.duration;
        } else {
          video.pause();
          setIsIntroVideoPlaying(false);
          handleIntroComplete();
        }
      }
    }
  };

  // 인트로 비디오 배속 변경
  const handleIntroPlaybackRateChange = () => {
    if (!introVideoRef.current) return;

    const video = introVideoRef.current;
    const rates = [0.75, 1, 1.25, 1.5, 1.75, 2];
    const currentIndex = rates.indexOf(introPlaybackRate);
    const nextIndex = (currentIndex + 1) % rates.length;
    const nextRate = rates[nextIndex];

    video.playbackRate = nextRate;
    setIntroPlaybackRate(nextRate);
  };

  // 인트로 비디오 일시정지/재생
  const handleIntroVideoAreaClick = (
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    if (
      event.target instanceof HTMLElement &&
      (event.target.tagName === "BUTTON" || event.target.closest("button"))
    ) {
      return;
    }

    const video = introVideoRef.current;
    if (!video) {
      return;
    }

    const isAtEnd =
      video.duration &&
      !isNaN(video.duration) &&
      video.currentTime >= video.duration - 0.1;

    if (video.paused) {
      if (isAtEnd) {
        video.currentTime = 0;
      }
      video.playbackRate = introPlaybackRate;
      safePlayVideo(video);
    } else {
      video.pause();
    }
  };

  // 종료 시 비디오 일시정지
  const handleExit = useCallback(() => {
    pauseAllSelectionVideos();
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (inactivityModalTimerRef.current) {
      clearInterval(inactivityModalTimerRef.current);
      inactivityModalTimerRef.current = null;
    }
    onExit();
  }, [onExit]);

  // 영상 재생 중인지 확인
  const isAnyVideoPlaying = useCallback((): boolean => {
    // 인트로 영상 재생 중
    if (view === "intro" && isIntroVideoPlaying) {
      return true;
    }

    // 선택지 영상 재생 중
    if (Object.values(playingSelectionVideos).some((playing) => playing)) {
      return true;
    }

    // 결과 영상 재생 중
    if (
      gamePhase === "result" &&
      resultVideoRef.current &&
      !resultVideoRef.current.paused &&
      !resultVideoRef.current.ended &&
      !isResultVideoComplete
    ) {
      return true;
    }

    // 최종 결과 영상 재생 중
    if (
      view === "complete" &&
      finalVideoRef.current &&
      !finalVideoRef.current.paused &&
      !finalVideoRef.current.ended &&
      !isFinalVideoComplete
    ) {
      return true;
    }

    return false;
  }, [
    view,
    gamePhase,
    isIntroVideoPlaying,
    playingSelectionVideos,
    isResultVideoComplete,
    isFinalVideoComplete,
  ]);

  // 선택 모달 타이머 시작
  const startInactivityModalTimer = useCallback(() => {
    if (inactivityModalTimerRef.current) {
      clearInterval(inactivityModalTimerRef.current);
    }

    setInactivityModalCountdown(5);

    // 카운트다운 함수
    const tick = () => {
      setInactivityModalCountdown((prev) => {
        if (prev <= 1) {
          // 5초 경과 시 자동으로 초기화면으로 복귀
          if (inactivityModalTimerRef.current) {
            clearInterval(inactivityModalTimerRef.current);
            inactivityModalTimerRef.current = null;
          }
          setTimeout(() => {
            handleExit();
          }, 0);
          return 0;
        }
        return prev - 1;
      });
    };

    setTimeout(() => {
      tick();
      inactivityModalTimerRef.current = setInterval(tick, 1000);
    }, 1000);
  }, [handleExit]);

  // 비활성 타이머 시작
  const startInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    inactivityTimerRef.current = setTimeout(() => {
      if (!isAnyVideoPlaying()) {
        setShowInactivityModal(true);
        startInactivityModalTimer();
      } else {
        startInactivityTimer();
      }
    }, 10000);
  }, [isAnyVideoPlaying, startInactivityModalTimer]);

  // 사용자 활동 감지
  const handleUserActivity = useCallback(() => {
    lastActivityTimeRef.current = Date.now();
    if (showInactivityModal) {
      return;
    }
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    startInactivityTimer();
  }, [showInactivityModal, startInactivityTimer]);

  // 계속 진행
  const handleContinuePlaying = () => {
    if (inactivityModalTimerRef.current) {
      clearInterval(inactivityModalTimerRef.current);
      inactivityModalTimerRef.current = null;
    }
    setShowInactivityModal(false);
    setInactivityModalCountdown(5);
    lastActivityTimeRef.current = Date.now();
    startInactivityTimer();
  };

  // 비활성 상태 감지 초기화
  useEffect(() => {
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    events.forEach((event) => {
      window.addEventListener(event, handleUserActivity, { passive: true });
    });

    startInactivityTimer();

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleUserActivity);
      });
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      if (inactivityModalTimerRef.current) {
        clearInterval(inactivityModalTimerRef.current);
        inactivityModalTimerRef.current = null;
      }
    };
  }, [view, handleUserActivity, startInactivityTimer]);

  if (!gameConfig) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center text-lg font-semibold text-gray-600">
        진행할 미션을 선택해주세요.
      </div>
    );
  }

  if (view === "intro") {
    return (
      <div className="relative flex min-h-screen w-full items-center justify-center overflow-y-auto bg-gradient-to-br from-slate-900 via-gray-900 to-slate-900 py-6">
        <InactivityModal
          show={showInactivityModal}
          countdown={inactivityModalCountdown}
          onContinue={handleContinuePlaying}
        />
        <div className="relative flex w-full max-w-7xl flex-col gap-5 rounded-[1.25rem] border-4 border-cyan-400/70 bg-black/40 px-48 py-8 shadow-[0_35px_120px_rgba(6,182,212,0.4)] backdrop-blur-[28px] ring-2 ring-cyan-500/20">
          <div className="flex flex-col items-center gap-4 text-center">
            <span className="inline-flex items-center gap-3 rounded-full border border-cyan-400/50 bg-cyan-500/10 backdrop-blur-sm px-4 py-2 text-sm font-semibold uppercase tracking-[0.6em] text-cyan-300">
              <span className="h-2 w-2 rounded-full bg-cyan-400" />
              {gameConfig.badgeLabel}
            </span>
            <h1 className="text-[3.25rem] leading-tight font-extrabold text-white drop-shadow-[0_18px_45px_rgba(6,182,212,0.3)]">
              {gameConfig.heroEmoji && (
                <span className="mr-2">{gameConfig.heroEmoji}</span>
              )}
              <LineBreakText value={gameConfig.heroTitle} />
            </h1>
          </div>

          <div
            className="relative aspect-video w-full overflow-hidden rounded-[1.25rem] border border-cyan-500/50 shadow-[0_30px_90px_rgba(6,182,212,0.3)] cursor-pointer"
            onClick={handleIntroVideoAreaClick}
          >
            <div className="pointer-events-none absolute inset-0 rounded-[1.25rem] border border-cyan-400/30 mix-blend-screen opacity-50" />
            {gameConfig.introVideo ? (
              <>
                <video
                  key={gameConfig.introVideo}
                  ref={introVideoRef}
                  src={gameConfig.introVideo}
                  poster={gameConfig.introVideoPoster}
                  autoPlay
                  playsInline
                  className="h-full w-full object-cover border-4 border-cyan-500/50 rounded-[1.25rem]"
                  onLoadedData={async () => {
                    setIsIntroVideoReady(true);
                    if (introVideoRef.current) {
                      await safePlayVideo(introVideoRef.current);
                    }
                  }}
                  onPlay={() => {
                    const video = introVideoRef.current;
                    if (video) {
                      video.playbackRate = introPlaybackRate;
                    }
                    setIsIntroVideoPlaying(true);
                  }}
                  onPause={() => {
                    setTimeout(() => {
                      if (introVideoRef.current?.paused) {
                        setIsIntroVideoPlaying(false);
                      }
                    }, 100);
                  }}
                  onEnded={() => setIsIntroVideoPlaying(false)}
                />
                {isIntroVideoReady && !isIntroVideoPlaying && (
                  <button
                    type="button"
                    onClick={handleIntroVideoPlay}
                    className="absolute inset-0 flex items-center justify-center rounded-[1.25rem] bg-black/35 opacity-0 transition-opacity duration-200 hover:opacity-100"
                  >
                    <span className="flex h-20 w-20 items-center justify-center rounded-full bg-white/90 text-cyan-500 shadow-lg">
                      <Play className="h-12 w-12" />
                    </span>
                  </button>
                )}
                {isIntroVideoPlaying && (
                  <>
                    <button
                      onClick={handleIntroPlaybackRateChange}
                      className="group absolute bottom-7 right-7 z-10 flex justify-center items-center gap-3 rounded-full bg-black/40 hover:bg-gradient-to-r hover:from-cyan-600 hover:via-cyan-500 hover:to-cyan-600 px-6 py-3.5 text-base font-semibold text-white/80 hover:text-white transition-all duration-300 hover:shadow-[0_4px_16px_rgba(6,182,212,0.6)] hover:-translate-y-0.5 backdrop-blur-md border border-cyan-400/30 hover:border-cyan-400/50 overflow-hidden opacity-60 hover:opacity-100"
                    >
                      <Gauge className="h-6 w-6" />
                      <span>{introPlaybackRate}x</span>
                    </button>
                    <button
                      onClick={handleIntroSkip}
                      className="group absolute top-7 right-7 z-10 flex justify-center items-center gap-3 rounded-full bg-black/40 hover:bg-gradient-to-r hover:from-cyan-600 hover:via-cyan-500 hover:to-cyan-600 px-6 py-3.5 text-base font-semibold text-white/80 hover:text-white transition-all duration-300 hover:shadow-[0_4px_16px_rgba(6,182,212,0.6)] hover:-translate-y-0.5 backdrop-blur-md border border-cyan-400/30 hover:border-cyan-400/50 overflow-hidden opacity-60 hover:opacity-100"
                    >
                      <span>SKIP</span>
                      <SkipForward className="h-6 w-6 transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                  </>
                )}
              </>
            ) : (
              <div className="flex h-full min-h-[870px] w-full items-center justify-center bg-slate-900/80 text-xl font-semibold text-gray-300">
                인트로 영상이 준비 중입니다.
              </div>
            )}
          </div>

          <div className="flex flex-col items-center gap-2">
            <button
              onClick={handleIntroComplete}
              className="group relative flex items-center gap-3 overflow-hidden rounded-[1.25rem] bg-gradient-to-r from-cyan-600 via-cyan-500 to-cyan-600 hover:from-cyan-500 hover:via-cyan-400 hover:to-cyan-500 px-12 py-5 text-base font-bold uppercase tracking-[0.35em] text-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_32px_85px_rgba(6,182,212,0.4)] shadow-lg shadow-cyan-500/20"
            >
              <span className="relative z-10 flex items-center gap-3 text-xl">
                {gameConfig.selectionCta}
                <ChevronRight className="h-6 w-6 transition-transform duration-300 group-hover:translate-x-2" />
              </span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (view === "complete" && finalOutcome) {
    return (
      <div className="flex flex-col items-center justify-center w-full min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-slate-900 p-8">
        <InactivityModal
          show={showInactivityModal}
          countdown={inactivityModalCountdown}
          onContinue={handleContinuePlaying}
        />
        <div className="w-full max-w-7xl space-y-5">
          <div
            className={`relative overflow-hidden rounded-[1.25rem] border-4 ${outcomeTheme.border} bg-gradient-to-br from-slate-800/90 via-slate-800/80 to-slate-900/90 shadow-[0_35px_120px_rgba(6,182,212,0.3)] ring-2 ring-cyan-500/20 backdrop-blur-sm`}
          >
            <div className="relative flex justify-start rounded-[1.25rem] px-[30px] py-[20px]">
              <div className="flex w-full flex-col gap-5 text-left">
                <div className="flex flex-wrap items-center justify-start gap-2">
                  <span
                    className={`inline-flex items-center rounded-full border px-5 py-2 text-base font-semibold uppercase tracking-[0.35em] shadow-sm backdrop-blur-sm ${outcomeTheme.badge}`}
                  >
                    {finalOutcome.title ?? "작전 결과"}
                  </span>
                </div>

                <div className="relative inline-block">
                  <h2
                    className={`text-[2rem] font-extrabold uppercase leading-tight ${outcomeTheme.headingColor} ${outcomeTheme.headingShadow}`}
                  >
                    {finalOutcome.descriptionTitle ?? "작전이 종료되었습니다."}
                  </h2>
                </div>

                <div className="flex flex-wrap items-center gap-6">
                  {finalStatItems.map(
                    ({
                      id,
                      icon: Icon,
                      text,
                      chipClass,
                      textClass,
                      iconClass,
                      iconWrapClass,
                    }) => (
                      <div
                        key={id}
                        className={`group flex items-center gap-2 rounded-full px-4 py-3 text-sm font-semibold shadow-sm backdrop-blur-md transition-all duration-200 hover:-translate-y-1 hover:shadow-lg ${chipClass}`}
                      >
                        <span
                          className={`flex h-5 w-5 items-center justify-center rounded-full ${iconWrapClass}`}
                        >
                          <Icon className={`h-3 w-3 ${iconClass}`} />
                        </span>
                        <span className={`tracking-wide ${textClass}`}>
                          {text}
                        </span>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>

          <div
            className="relative overflow-hidden rounded-[1.25rem] py-5 shadow-sm border-4 border-cyan-500/50 max-h-[460px] w-full aspect-video cursor-pointer"
            onClick={handleFinalVideoAreaClick}
          >
            {finalOutcome.video ? (
              <>
                <video
                  key={finalOutcome.video}
                  ref={finalVideoRef}
                  src={finalOutcome.video}
                  autoPlay
                  onLoadedData={async () => {
                    if (finalVideoRef.current) {
                      const success = await safePlayVideo(
                        finalVideoRef.current
                      );
                      if (success) {
                        setIsFinalVideoPlaying(true);
                      }
                    }
                  }}
                  onPlay={() => {
                    setIsFinalVideoComplete(false);
                    setIsFinalVideoPlaying(true);
                  }}
                  onPause={() => {
                    setTimeout(() => {
                      if (finalVideoRef.current?.paused) {
                        setIsFinalVideoPlaying(false);
                      }
                    }, 100);
                  }}
                  onEnded={() => {
                    setIsFinalVideoComplete(true);
                    setIsFinalVideoPlaying(false);
                  }}
                  onError={() => {
                    setIsFinalVideoComplete(true);
                    setIsFinalVideoPlaying(false);
                  }}
                  className="block h-full w-full object-contain"
                />
                {!isFinalVideoPlaying && !isFinalVideoComplete && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (finalVideoRef.current) {
                        safePlayVideo(finalVideoRef.current).then((success) => {
                          if (success) {
                            setIsFinalVideoPlaying(true);
                          }
                        });
                      }
                    }}
                    className="absolute inset-0 z-[20] flex items-center justify-center rounded-[1.25rem] bg-black/35 opacity-0 transition-opacity duration-200 hover:opacity-100"
                  >
                    <span className="flex h-20 w-20 items-center justify-center rounded-full bg-white/90 text-cyan-500 shadow-lg">
                      <Play className="h-12 w-12" />
                    </span>
                  </button>
                )}
                {isFinalVideoPlaying && !isFinalVideoComplete && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFinalVideoSkip();
                    }}
                    className="group absolute top-4 right-4 z-[20] flex justify-center items-center gap-2.5 rounded-full bg-black/40 hover:bg-gradient-to-r hover:from-cyan-600 hover:via-cyan-500 hover:to-cyan-600 px-8 py-4 text-2xl font-semibold text-white/80 hover:text-white transition-all duration-300 hover:shadow-[0_4px_12px_rgba(6,182,212,0.6)] hover:-translate-y-0.5 backdrop-blur-md border border-cyan-400/30 hover:border-cyan-400/50 overflow-hidden opacity-60 hover:opacity-100"
                  >
                    <span>SKIP</span>
                    <SkipForward className="h-8 w-8 transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                )}
              </>
            ) : (
              <div className="flex aspect-video w-full items-center justify-center bg-slate-900/80 text-lg font-semibold text-gray-300">
                최종 결과 영상이 준비 중입니다.
              </div>
            )}

            {(!finalOutcome.video || isFinalVideoComplete) && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                  <button
                    onClick={handleExit}
                    className="group relative flex items-center gap-5 overflow-hidden rounded-[1.25rem] border-4 border-cyan-400/50 bg-gradient-to-r from-cyan-600 via-cyan-500 to-cyan-600 hover:from-cyan-500 hover:via-cyan-400 hover:to-cyan-500 px-12 py-7 text-3xl font-black uppercase tracking-[0.4em] text-white shadow-[0_22px_45px_rgba(6,182,212,0.4)] transition-transform duration-300 hover:-translate-y-1 hover:shadow-[0_30px_65px_rgba(6,182,212,0.5)]"
                  >
                    <span className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-40 bg-white/10" />
                    <span className="pointer-events-none absolute -left-1/2 top-1/2 h-[160%] w-[160%] -translate-y-1/2 rotate-12 bg-white/15 opacity-0 blur-3xl transition duration-500 group-hover:translate-x-[140%] group-hover:opacity-70" />
                    <span className="pointer-events-none absolute inset-0 border border-white/40 mix-blend-screen opacity-30" />
                    <span className="relative z-10 flex items-center gap-4">
                      작전종료
                      <ChevronRight className="h-9 w-9 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </button>
                </div>
              )}
          </div>

          {finalOutcome.description && (
            <div className="mt-10 w-full">
              <div className="relative mx-auto max-w-7xl rounded-[1.25rem] border-4 border-cyan-400/70 bg-gradient-to-br from-slate-800/90 via-slate-800/80 to-slate-900/90 px-[35px] py-[25px] shadow-[0_35px_120px_rgba(6,182,212,0.3)] ring-2 ring-cyan-500/20 backdrop-blur-sm">
                <div className="space-y-2 text-white">
                  <p className="text-[1.75rem] font-semibold leading-relaxed">
                    {finalOutcome.description}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="py-5 px-10 flex flex-col items-center justify-center w-full min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-slate-900">
      <div className="w-full relative flex flex-col items-center justify-center">
        <div className="w-full max-w-7xl space-y-[1.75rem]">
          <div className="w-full">
            <div className="bg-gradient-to-br from-slate-800/90 via-slate-800/80 to-slate-900/90 rounded-[1.25rem] px-[40px] py-[30px] border-2 border-cyan-400/70 shadow-[0_35px_120px_rgba(6,182,212,0.3)] ring-2 ring-cyan-500/20 backdrop-blur-sm">
              <div className="flex items-center justify-between mb-4 gap-5">
                <div className="flex items-center gap-6">
                  <div className="w-10 h-10 rounded-full bg-cyan-500 flex items-center justify-center shadow-lg shadow-cyan-500/30">
                    <span className="text-white font-semibold text-lg">
                      {currentScene + 1}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <h2 className="text-[1.75rem] font-bold text-white">
                      {currentScenario?.title}
                    </h2>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="font-bold text-cyan-400 text-[1.75rem]">
                    STEP {currentScene + 1}/{totalScenes || 0}
                  </span>
                </div>
              </div>
              <div className="w-full bg-slate-700/50 rounded-full h-2 mt-5">
                <div
                  className="bg-gradient-to-r from-[#D9F3FF] to-[#008FD3] h-full rounded-full transition-all duration-500"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          </div>

          {gamePhase === "selection" && (
            <div className="w-full relative z-10">
              <div className="grid gap-6 grid-cols-2">
                {choices.map((choice, idx) => {
                  const selectionSrc = choice.video?.selection;
                  const refKey = `${currentScene}-${idx}`;
                  const isSelectionReady = selectionVideoReady[refKey] === true;
                  const videoElement = selectionVideoRefs.current[refKey];
                  const isVideoLoading = videoElement
                    ? videoElement.readyState < 2
                    : true;
                  const shouldShowLoading =
                    !isSelectionReady &&
                    isVideoLoading &&
                    !playingSelectionVideos[refKey];
                  return (
                    <div
                      key={idx}
                      className="relative flex h-full flex-col gap-4 rounded-[1.25rem] border border-cyan-500/50 bg-slate-800/60 hover:bg-slate-800/80 p-5 transition-transform shadow-sm hover:shadow-[0_18px_50px_rgba(6,182,212,0.3)]"
                    >
                      <div
                        className="relative overflow-hidden rounded-[1.25rem] shadow-inner aspect-video cursor-pointer"
                        onClick={(e) =>
                          handleSelectionVideoAreaClick(e, refKey, idx)
                        }
                      >
                        {selectionSrc ? (
                          <>
                            <video
                              key={selectionSrc}
                              ref={(node) => {
                                if (node) {
                                  selectionVideoRefs.current[refKey] = node;
                                } else {
                                  delete selectionVideoRefs.current[refKey];
                                }
                              }}
                              src={selectionSrc}
                              playsInline
                              className="block h-full w-full object-cover rounded-[1.25rem] aspect-auto"
                              onLoadedData={() =>
                                setSelectionVideoReady((prev) => ({
                                  ...prev,
                                  [refKey]: true,
                                }))
                              }
                              onPlay={() =>
                                setPlayingSelectionVideos((prev) => ({
                                  ...prev,
                                  [refKey]: true,
                                }))
                              }
                              onPause={() =>
                                setPlayingSelectionVideos((prev) => ({
                                  ...prev,
                                  [refKey]: false,
                                }))
                              }
                              onEnded={() => {
                                setPlayingSelectionVideos((prev) => ({
                                  ...prev,
                                  [refKey]: false,
                                }));
                                setWatchedSelectionVideos((prev) => ({
                                  ...prev,
                                  [refKey]: true,
                                }));
                              }}
                            />
                            {shouldShowLoading && (
                              <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80 backdrop-blur-sm">
                                <div className="flex flex-col items-center gap-3">
                                  <Loader2 className="h-12 w-12 animate-spin text-cyan-400" />
                                  <span className="text-lg font-semibold uppercase tracking-[0.35em] text-gray-300">
                                    Loading...
                                  </span>
                                </div>
                              </div>
                            )}
                            {playingSelectionVideos[refKey] && (
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleSelectionVideoSkip(idx);
                                }}
                                className="group absolute top-4 right-4 z-10 flex justify-center items-center gap-2 rounded-full bg-black/40 hover:bg-gradient-to-r hover:from-cyan-600 hover:via-cyan-500 hover:to-cyan-600 px-5 py-2.5 text-base font-semibold text-white/80 hover:text-white transition-all duration-300 hover:shadow-[0_4px_12px_rgba(6,182,212,0.6)] hover:-translate-y-0.5 backdrop-blur-md border border-cyan-400/30 hover:border-cyan-400/50 overflow-hidden opacity-60 hover:opacity-100"
                              >
                                <span>SKIP</span>
                                <SkipForward className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                              </button>
                            )}
                            {!playingSelectionVideos[refKey] &&
                              isSelectionReady && (
                                <button
                                  type="button"
                                  onClick={(event) =>
                                    handleSelectionVideoOverlayClick(
                                      event,
                                      refKey
                                    )
                                  }
                                  className="absolute inset-0 flex items-center justify-center bg-black/35 opacity-0 transition-opacity duration-200 hover:opacity-100"
                                >
                                  <span className="flex h-20 w-20 items-center justify-center rounded-full bg-white/90 text-cyan-500 shadow-lg">
                                    <Play className="h-12 w-12" />
                                  </span>
                                </button>
                              )}
                          </>
                        ) : (
                          <div className="flex aspect-video w-full items-center justify-center bg-slate-900/80 text-base font-semibold text-gray-300">
                            <Loader2 className="w-10 h-10 animate-spin text-cyan-400" />
                          </div>
                        )}
                      </div>

                      <div className="flex items-center justify-center gap-4">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-cyan-500 text-base font-bold text-white shadow shadow-cyan-500/30">
                          {idx + 1}
                        </div>
                        <h3 className="flex-1 text-xl font-semibold leading-snug text-white">
                          {choice.text}
                        </h3>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleChoiceConfirm(idx)}
                        className="mt-auto group flex items-center justify-between rounded-[1.25rem] bg-slate-700/60 hover:bg-cyan-500/20 text-white hover:text-cyan-300 border-4 border-cyan-500/50 hover:border-cyan-400 px-5 py-3 text-lg font-semibold shadow shadow-cyan-500/20 transition-all duration-300"
                      >
                        <span>이 방법으로 진행</span>
                        <ChevronRight className="h-6 w-6 text-cyan-400 group-hover:text-cyan-300 transition-colors" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 선택지 비디오 모달 */}
          <SelectionVideoModal
            isOpen={
              gamePhase === "selection" &&
              modalSelectionIndex !== null &&
              choices[modalSelectionIndex] !== undefined
            }
            choice={
              modalSelectionIndex !== null
                ? choices[modalSelectionIndex]
                : undefined
            }
            choiceText={
              modalSelectionIndex !== null
                ? choices[modalSelectionIndex]?.text || ""
                : ""
            }
            currentScene={currentScene}
            pendingChoiceIndex={pendingChoiceIndex}
            modalSelectionIndex={modalSelectionIndex}
            onClose={handleCloseModal}
            onVideoEnd={(refKey) => {
              setWatchedSelectionVideos((prev) => ({
                ...prev,
                [refKey]: true,
              }));
            }}
            onSkip={() => {
              if (
                pendingChoiceIndex !== null &&
                modalSelectionIndex !== null &&
                pendingChoiceIndex === modalSelectionIndex
              ) {
                const choice = currentScenario?.choices?.[modalSelectionIndex];
                if (choice) {
                  const refKey = `${currentScene}-${modalSelectionIndex}`;
                  setWatchedSelectionVideos((prev) => ({
                    ...prev,
                    [refKey]: true,
                  }));
                  setPendingChoiceIndex(null);
                  setSelectedChoiceIndex(modalSelectionIndex);
                  setGamePhase("result");
                  setIsResultVideoComplete(!choice?.video?.result);
                  setIsResultVideoPlaying(false);
                  handleCloseModal();
                }
              }
            }}
            onProceedToResult={() => {
              if (
                pendingChoiceIndex !== null &&
                modalSelectionIndex !== null &&
                pendingChoiceIndex === modalSelectionIndex
              ) {
                const choice = currentScenario?.choices?.[modalSelectionIndex];
                if (choice) {
                  setPendingChoiceIndex(null);
                  setSelectedChoiceIndex(modalSelectionIndex);
                  setGamePhase("result");
                  setIsResultVideoComplete(!choice?.video?.result);
                  setIsResultVideoPlaying(false);
                }
              }
            }}
            safePlayVideo={safePlayVideo}
          />

          {gamePhase === "result" && (
            <div className="w-full relative z-10">
              <div className="mx-auto flex max-w-7xl flex-col gap-5 rounded-[1.25rem] border border-cyan-500/50 bg-gradient-to-br from-slate-800/90 via-slate-800/80 to-slate-900/90 p-6 relative shadow-[0_35px_120px_rgba(6,182,212,0.3)] ring-2 ring-cyan-500/20 backdrop-blur-sm">
                <div
                  className="relative overflow-hidden rounded-[1.25rem] aspect-video max-h-[450px] cursor-pointer flex items-center justify-center"
                  onClick={handleResultVideoAreaClick}
                >
                  {selectedChoice?.video?.result ? (
                    <>
                      <video
                        key={selectedChoice.video.result}
                        ref={resultVideoRef}
                        src={selectedChoice.video.result}
                        autoPlay
                        playsInline
                        onLoadedData={async () => {
                          if (resultVideoRef.current) {
                            const success = await safePlayVideo(
                              resultVideoRef.current
                            );
                            if (success) {
                              setIsResultVideoPlaying(true);
                            }
                          }
                        }}
                        onPlay={() => {
                          setIsResultVideoComplete(false);
                          setIsResultVideoPlaying(true);
                        }}
                        onPause={() => {
                          setTimeout(() => {
                            if (resultVideoRef.current?.paused) {
                              setIsResultVideoPlaying(false);
                            }
                          }, 100);
                        }}
                        onEnded={() => {
                          setIsResultVideoComplete(true);
                          setIsResultVideoPlaying(false);
                        }}
                        onError={() => {
                          setIsResultVideoComplete(true);
                          setIsResultVideoPlaying(false);
                        }}
                        className="block h-full w-full object-contain"
                      />
                      {!isResultVideoPlaying && !isResultVideoComplete && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (resultVideoRef.current) {
                              safePlayVideo(resultVideoRef.current).then(
                                (success) => {
                                  if (success) {
                                    setIsResultVideoPlaying(true);
                                  }
                                }
                              );
                            }
                          }}
                          className="absolute inset-0 z-[20] flex items-center justify-center rounded-[1.25rem] bg-black/35 opacity-0 transition-opacity duration-200 hover:opacity-100"
                        >
                          <span className="flex h-20 w-20 items-center justify-center rounded-full bg-white/90 text-cyan-500 shadow-lg">
                            <Play className="h-12 w-12" />
                          </span>
                        </button>
                      )}
                      {isResultVideoPlaying && !isResultVideoComplete && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleResultVideoSkip();
                          }}
                          className="group absolute top-1 right-0 z-[20] flex justify-center items-center gap-2.5 rounded-full bg-black/40 hover:bg-gradient-to-r hover:from-cyan-600 hover:via-cyan-500 hover:to-cyan-600 px-8 py-4 text-2xl font-semibold text-white/80 hover:text-white transition-all duration-300 hover:shadow-[0_4px_12px_rgba(6,182,212,0.6)] hover:-translate-y-0.5 backdrop-blur-md border border-cyan-400/30 hover:border-cyan-400/50 overflow-hidden opacity-60 hover:opacity-100"
                        >
                          <span>SKIP</span>
                          <SkipForward className="h-8 w-8 transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="flex aspect-video w-full items-center justify-center bg-slate-900/80 text-lg font-semibold text-gray-300">
                      결과 영상이 준비되지 않았습니다.
                    </div>
                  )}
                </div>

                {(isResultVideoComplete || !selectedChoice?.video?.result) && (
                  <div className="pointer-events-auto rounded-[1.25rem] absolute inset-0 z-10 flex items-center justify-center bg-black/60">
                    <div className="flex flex-row items-center justify-center gap-10 w-full max-w-xl">
                      <button
                        type="button"
                        onClick={handleResultGoBack}
                        className="group flex-1 rounded-[1.25rem] px-6 py-7 text-2xl font-extrabold uppercase tracking-[0.35em] transition-all duration-300 flex items-center justify-center gap-6 bg-gradient-to-r from-[#64748B] via-[#475569] to-[#334155] text-white shadow-[0_22px_45px_rgba(71,85,105,0.4)] hover:-translate-y-1 hover:shadow-[0_30px_65px_rgba(71,85,105,0.5)]"
                      >
                        <ChevronLeft className="h-9 w-9 transition-transform duration-300 group-hover:-translate-x-1" />
                        <span>이전으로</span>
                      </button>
                      <button
                        type="button"
                        onClick={handleResultContinue}
                        className={`group flex-1 rounded-[1.25rem] px-6 py-7 text-2xl font-extrabold uppercase tracking-[0.35em] transition-all duration-300 flex items-center justify-center gap-6 ${
                          selectedChoice?.next !== undefined
                            ? "bg-gradient-to-r from-cyan-600 via-cyan-500 to-cyan-600 hover:from-cyan-500 hover:via-cyan-400 hover:to-cyan-500 text-white shadow-[0_22px_45px_rgba(6,182,212,0.4)] hover:-translate-y-1 hover:shadow-[0_30px_65px_rgba(6,182,212,0.5)]"
                            : "cursor-not-allowed bg-slate-700 text-gray-400 opacity-60 shadow-none"
                        }`}
                      >
                        <span className="flex items-center gap-4">
                          {selectedChoice?.next === -1
                            ? "미션완료"
                            : "다음단계"}
                          <ChevronRight className="h-9 w-9 transition-transform duration-300 group-hover:translate-x-1" />
                        </span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <CaptionCard
          content={
            gamePhase === "selection"
              ? currentScenario?.situation
              : selectedChoice?.result
          }
          label={gamePhase === "selection" ? missionLabel : participantLabel}
        />
        <InactivityModal
          show={showInactivityModal}
          countdown={inactivityModalCountdown}
          onContinue={handleContinuePlaying}
        />
      </div>
    </div>
  );
};
